// Need to use the React-specific entry point to import createApi
/* eslint-disable no-param-reassign */
import { createApi } from '@reduxjs/toolkit/query/react';
import fetchBaseQuery from '@utilities/fetchBaseQuery';
import { transformResponse } from '@utilities/api';
import {
  CountryResponse,
  ValidateLicensePlateRequest,
  ValidateLicensePlateResponse,
} from '@shared/types';
import {
  SubscriptionTopics,
  UserSubscriptionsData,
} from '@shared/types/common';

// Define a service using a base URL and expected endpoints
export const commonApi = createApi({
  reducerPath: 'commonApi',
  baseQuery: fetchBaseQuery,
  tagTypes: ['UserSubscriptions'],
  endpoints: (builder) => ({
    /**
     * Validate license plate
     */
    validateLicensePlate: builder.mutation<
      ValidateLicensePlateResponse,
      ValidateLicensePlateRequest
    >({
      query: (body) => ({
        url: `license-plate/validate`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => response.exists,
    }),

    /**
     * Check users subscriptions
     */
    checkUserSubscriptions: builder.query<UserSubscriptionsData[], string>({
      query: (userUid) => ({
        url: `customerio/check?user=${userUid}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'UserSubscriptions', id: 'LIST' }],
      transformResponse,
    }),

    /**
     * Subscribe to newsletter
     */
    subscribeNewsletter: builder.mutation({
      query: (body) => ({
        url: `subscriptions`,
        method: 'POST',
        body: {
          ...body,
          ...(body.name && {
            fullName: body.name,
          }),
        },
      }),
      invalidatesTags: [{ type: 'UserSubscriptions', id: 'LIST' }],
    }),

    /**
     * Unsubscribe from newsletter
     */
    unsubscribeNewsletter: builder.mutation({
      query: ({ user, subscriptions }) => ({
        url: `customerio/unsubscribe`,
        method: 'POST',
        body: {
          user,
          subscriptions,
        },
      }),
      invalidatesTags: [{ type: 'UserSubscriptions', id: 'LIST' }],
    }),

    /**
     * Get Countries
     */
    getCountries: builder.query<CountryResponse, null>({
      query: () => 'countries',
      transformResponse,
    }),

    /**
     * Get Countries
     */
    getSubscriptionTopics: builder.query<SubscriptionTopics[], null>({
      query: () => ({
        url: 'customerio/get-subscription-topics',
        method: 'GET',
      }),
      transformResponse,
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useValidateLicensePlateMutation,
  useSubscribeNewsletterMutation,
  useUnsubscribeNewsletterMutation,
  useCheckUserSubscriptionsQuery,
  useGetCountriesQuery,
  useGetSubscriptionTopicsQuery,
} = commonApi;
